// -----------------------------------------------------------------------
// <copyright company="Microsoft Corporation">
// Copyright (c) Microsoft Corporation. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import type { IObservable, ISubscriptionOptions } from './observable.types';
import { Observer } from './observer';

/**
 * The Observable class is an implementation of the subject in the Observer pattern. Use Observable<T> to
 * represent strongly typed events. https://en.wikipedia.org/wiki/Observer_pattern.
 */
export class Observable<T> implements IObservable<T> {
  private readonly _observers = new Array<Observer<T>>();

  private readonly onCallbackError: ((e: unknown) => void) | undefined;

  /**
   * Creates an instance of Observable.
   *
   * @param onCallbackError - Function that will be executed if any callback errors when notifying.
   */
  // eslint-disable-next-line @typescript-eslint/parameter-properties
  public constructor(onCallbackError?: (e: unknown) => void) {
    this.onCallbackError = onCallbackError;
  }

  /**
   * Subscribe to events sent via the notify method.
   *
   * @param callback - Callback to recieve events.
   * @param options - Optional modifications to the subscription behavior. See ISubscriptionOptions.
   *
   * @returns Function to remove this subscription.
   */
  public subscribe(callback: (newValue: T) => void, options: ISubscriptionOptions = {}): () => void {
    if (!callback) {
      return () => {
        // no-op
      };
    }

    const observer = new Observer(callback);
    observer.unregisterOnNextCall = !!options.unregisterOnFirstCall;

    this._observers.push(observer);

    // Will return a function that can be used to remove the object from the observers list
    return this.internalRemove.bind(this, observer);
  }

  /**
   * Notify all Observers by calling their respective callback with the given data.
   *
   * @param newValue - Defines the data to send to all observers.
   */
  public notify(newValue: T): void {
    for (const obs of this._observers) {
      if (obs.willBeUnregistered) {
        continue;
      }

      try {
        obs.callback(newValue);
      } catch (e) {
        // Errors do not stop executing other callbacks,
        // but the observable creator may want to be
        // informed of this
        if (this.onCallbackError) {
          this.onCallbackError(e);
        }
      }

      if (obs.unregisterOnNextCall) {
        this.deferUnregister(obs);
      }
    }
  }

  /**
   * Removes all subscriptions at once by clearing the internal list of observers.
   */
  public unsubscribeAll(): void {
    this._observers.length = 0;
  }

  private deferUnregister(observer: Observer<T>): void {
    observer.unregisterOnNextCall = false;
    observer.willBeUnregistered = true;

    // Send the callback removal operation to the end of the scheduled
    // work in the JS main thread. This way we guaranteed that we will
    // not try to remove an observer while we are iterating over the
    // collection
    setTimeout(() => {
      this.internalRemove(observer);
    }, 0);
  }

  // This should only be called when not iterating over _observers to avoid callback skipping.
  // Removes an observer from the _observers Array.
  private internalRemove(observer: Observer<T>): boolean {
    const index = this._observers.indexOf(observer);

    if (index !== -1) {
      this._observers.splice(index, 1);
      return true;
    }

    return false;
  }
}
